/**
 * useGridFilters — the toolbar filter capability: which chips exist, and the
 * four ways a chip list changes. Pure derivation over state the rows hook owns.
 *
 *   gridFilters ─┐                        ┌─► filterableColumns   AddFilter
 *   columns ─────┼──► useGridFilters ─────┤   chips               FilterList
 *   updateFilters┘        (no state)      └─► add·update·remove·clear
 *
 *   CHIP RULES — all three drop a filter from the RENDERED list only;
 *   none of them rewrites `gridFilters`, so the filter still reaches
 *   the server / the local filter engine:
 *     f.id present            ─╳─▶  owned elsewhere (URL bar); do not fight it
 *     column not found        ─╳─▶  columns changed under a persisted filter
 *     customFilterType locked ─╳─▶  the page pins it; it is not a user chip
 *
 * Consumers call this hook independently (AddFilter and FilterList are
 * siblings); every returned value derives from the arguments, so two
 * instances cannot disagree.
 */
import { useCallback, useMemo } from 'react';
import type { ServerGridFilter } from '../shared/gridFilterTypes';
import type { FilterColumn } from '../toolbar/filters/filterItemTypes';

/** One rendered filter chip: the persisted filter plus the column it belongs to. */
type GridFilterChip = { filter: ServerGridFilter; column: FilterColumn };

export type UseGridFiltersOptions = {
  /** The grid's live column definitions, in display order. */
  columns: FilterColumn[];
  /** The filters currently applied — owned by `useGridRows`, read-only here. */
  gridFilters: ServerGridFilter[];
  /** The single writer back into that state. */
  updateFilters: (filters: ServerGridFilter[]) => void;
};

export type GridFiltersCapability = {
  /** Columns a user may add a filter for. */
  filterableColumns: FilterColumn[];
  /** The chips to render, in `gridFilters` order. */
  chips: GridFilterChip[];
  /** Every applied filter's field, in order — the seed for the add-filter draft. */
  activeFilterFields: string[];
  /** Replaces the filter list with exactly `fields`, keeping each field's existing filter. */
  setFilterFields: (fields: string[]) => void;
  /** Drops the filter on `field`. */
  removeFilter: (field: string) => void;
  /** Replaces the filter on `field`. */
  updateFilter: (field: string, filter: ServerGridFilter) => void;
  /** Drops every filter. */
  clearFilters: () => void;
};

export function useGridFilters({ columns, gridFilters, updateFilters }: UseGridFiltersOptions): GridFiltersCapability {
  const filterableColumns = useMemo(() => columns.filter((col) => !!col.filterable), [columns]);

  const activeFilterFields = useMemo(() => gridFilters.map((filter) => filter.field), [gridFilters]);

  const chips = useMemo(
    () =>
      gridFilters
        .filter((f) => !f.id)
        .map((filter) => ({ filter, column: columns.find((col) => col.field === filter.field) }))
        .filter((pair): pair is GridFilterChip => !!pair.column && pair.column.customFilterType !== 'locked'),
    [gridFilters, columns]
  );

  const setFilterFields = useCallback(
    (fields: string[]) => {
      const newFilters: ServerGridFilter[] = fields.map((field) => {
        const existingFilter = gridFilters.find((filter) => filter.field === field);
        if (existingFilter) return existingFilter;
        return { field, where: [] };
      });
      updateFilters(newFilters);
    },
    [gridFilters, updateFilters]
  );

  const removeFilter = useCallback(
    (field: string) => {
      updateFilters(gridFilters.filter((item) => item.field !== field));
    },
    [gridFilters, updateFilters]
  );

  const updateFilter = useCallback(
    (field: string, filter: ServerGridFilter) => {
      updateFilters(gridFilters.map((item) => (item.field === field ? filter : item)));
    },
    [gridFilters, updateFilters]
  );

  const clearFilters = useCallback(() => updateFilters([]), [updateFilters]);

  return { filterableColumns, chips, activeFilterFields, setFilterFields, removeFilter, updateFilter, clearFilters };
}
